// Guías de /primeros-pasos (y vídeo de la banda «Primeros pasos» de la portada).
//
// Cada guía:
//   provider   'youtube' | 'tiktok'
//   videoId    ID del vídeo en la plataforma; null = «Próximamente» (aún sin publicar)
//   format     'horizontal' (16:9) | 'vertical' (9:16)
//   duration   'm:ss', o null si no se conoce (no se muestra)
//   difficulty clave de DIFFICULTY
//   thumbnail  portada propia en public/guides-thumbnails/ (nunca la de la plataforma)
//   featured   la guía destacada de la cabecera
//
// Los reproductores solo se cargan al pedirlo el jugador (ver components/VideoModal.jsx).

export const TIKTOK_ACCOUNT = 'frostboundwow';

export const PROVIDERS = {
  youtube: { label: 'YouTube' },
  tiktok: { label: 'TikTok' },
};

// Modo de privacidad mejorada y sin autoplay: el jugador pulsa play en el reproductor
// (YouTube no cuenta como visualización lo que arranca solo).
export const youtubeEmbed = (id) =>
  `https://www.youtube-nocookie.com/embed/${id}?rel=0&playsinline=1`;

// Embed Player oficial (developers.tiktok.com/doc/embed-player). rel=0: al terminar
// sugiere otros vídeos de la cuenta en vez de recomendados.
export const tiktokEmbed = (id) =>
  `https://www.tiktok.com/player/v1/${id}?autoplay=0&rel=0&music_info=0&description=0`;

export const playerUrl = (guide) =>
  guide.provider === 'tiktok' ? tiktokEmbed(guide.videoId) : youtubeEmbed(guide.videoId);

export const platformUrl = (guide) => {
  if (!guide.videoId) return null;
  return guide.provider === 'tiktok'
    ? `https://www.tiktok.com/@${TIKTOK_ACCOUNT}/video/${guide.videoId}`
    : `https://www.youtube.com/watch?v=${guide.videoId}`;
};

export const DIFFICULTY = {
  basic: {
    label: 'BÁSICO',
    color: '#69CCF0',
    bg: 'rgba(0,120,255,0.18)',
    border: 'rgba(0,120,255,0.45)',
  },
  intermediate: {
    label: 'INTERMEDIO',
    color: '#B080FF',
    bg: 'rgba(160,50,255,0.15)',
    border: 'rgba(160,50,255,0.45)',
  },
  advanced: {
    label: 'AVANZADO',
    color: '#FFAA44',
    bg: 'rgba(255,140,0,0.15)',
    border: 'rgba(255,140,0,0.45)',
  },
};

// Orden: el del recorrido «Primeros pasos» (RRSS/Tutoriales/00_LISTA_PILOTO.md);
// los que quedan fuera del recorrido básico van detrás.
export const GUIDES = [
  {
    id: 'sube-tus-alters',
    title: 'SUBE A TUS ALTERS CONTIGO',
    description:
      'Lleva a tus alters a tu nivel con .lvlalter, comparte tus misiones con .syncquests y prepara el botín, el equipo y el mantenimiento de tu Warband mientras subes con ella.',
    provider: 'tiktok',
    videoId: '7702515822610107670',
    format: 'vertical',
    duration: '2:59', // export «Gestion de alters.mp4» preparado para subir (179,4 s)
    difficulty: 'intermediate',
    thumbnail: '/guides-thumbnails/t15-alters.jpg',
    featured: true,
  },
  {
    id: 'crea-tu-warband',
    title: 'CREA TU WARBAND',
    description:
      'Al entrar por primera vez se abre la ventana «Crea tu Warband»: elige raza, clase y sexo y crea tus alters en segundos. Si ya la cerraste, escribe /warband.',
    provider: 'tiktok',
    videoId: '7693503518354771222',
    format: 'vertical',
    duration: '0:32',
    difficulty: 'basic',
    thumbnail: '/guides-thumbnails/t11-warband.jpg',
    featured: false,
  },
  {
    id: 'estrategias-bots',
    title: 'LAS ESTRATEGIAS DE TUS BOTS',
    description:
      'Qué activar en el menú de cada bot para que tu tanque, tu sanador y tus DPS hagan bien su papel.',
    provider: 'tiktok',
    videoId: null,
    format: 'vertical',
    duration: null,
    difficulty: 'intermediate',
    thumbnail: '/guides-thumbnails/t14-estrategias.jpg',
    featured: false,
  },
  {
    id: 'companeros-por-clase',
    title: 'COMPAÑEROS POR CLASE',
    description:
      '¿Te falta un sanador o un tanque? Con el Creator de MultiBot añades un compañero temporal de la clase que quieras, a tu nivel, sin gastar plazas de personaje.',
    provider: 'tiktok',
    videoId: '7696583694005619971',
    format: 'vertical',
    duration: null, // PENDIENTE: duración del vídeo publicado
    difficulty: 'intermediate',
    thumbnail: '/guides-thumbnails/t04-creator.jpg',
    featured: false,
  },
  {
    id: 'transfiguracion',
    title: 'TRANSFIGURACIÓN EN 30 SEGUNDOS',
    description:
      'Cambia la apariencia de tu equipo con el Warpweaver: elige la ranura, una apariencia de tu colección y listo.',
    provider: 'tiktok',
    videoId: null,
    format: 'vertical',
    duration: null,
    difficulty: 'basic',
    thumbnail: '/guides-thumbnails/t12-transfiguracion.jpg',
    featured: false,
  },
  {
    id: 'mitica-plus',
    title: 'MÍTICA+ EN UN CLIC',
    description:
      'Juega una Mítica+ con tus alters o con bots desde la ventana de MultiBot: elige mazmorra y nivel, completa el grupo y empieza.',
    provider: 'tiktok',
    videoId: null,
    format: 'vertical',
    duration: null,
    difficulty: 'advanced',
    thumbnail: '/guides-thumbnails/t13-mitica.jpg',
    featured: false,
  },
  {
    id: 'invocar-warband',
    number: '01',
    title: 'CÓMO INVOCAR TU WARBAND',
    description:
      'Aprende a invocar a tus 4 compañeros de IA, gestionarlos y formar tu Warband desde el primer minuto en Frostbound.',
    provider: 'youtube',
    videoId: 'witMfHGGnG0',
    format: 'horizontal',
    duration: '0:40',
    difficulty: 'basic',
    thumbnail: '/guides-thumbnails/01-warband.jpg',
    featured: false,
  },
  {
    id: 'sistema-combate',
    number: '02',
    title: 'SISTEMA DE COMBATE',
    description:
      'Domina las mecánicas de combate de Frostbound: Estrategias de combate, formaciones y funciones de movimiento.',
    provider: 'youtube',
    videoId: 'vUK053bqNiY',
    format: 'horizontal',
    duration: '1:15',
    difficulty: 'basic',
    thumbnail: '/guides-thumbnails/02-combate.jpg',
    featured: false,
  },
];

export const getFeaturedGuide = () => GUIDES.find((g) => g.featured) ?? GUIDES[0];

export const GUIDES_THUMBNAIL_FALLBACK = '/guides-thumbnails/default.jpg';
